import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { ensureAuthorizedStaffSession } from "@/core/services/auth.service";
import {
  INVITE_AUTH_CALLBACK_PATH,
  SET_PASSWORD_PATH,
} from "@/lib/auth-routes";
import { getSupabaseClient } from "@/lib/supabase";
import { markMustSetPassword } from "@/types/auth";

function resolveNextPath(next: string | null, pathname: string): string {
  if (
    pathname === INVITE_AUTH_CALLBACK_PATH ||
    pathname.endsWith(INVITE_AUTH_CALLBACK_PATH)
  ) {
    return SET_PASSWORD_PATH;
  }

  if (next?.startsWith("/") && !next.startsWith("//")) {
    return next;
  }

  return "/dashboard";
}

function clearAuthHashFromUrl() {
  const cleanUrl = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState({}, document.title, cleanUrl);
}

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState(false);

  useEffect(() => {
    const supabase = getSupabaseClient();
    const code = searchParams.get("code");
    const next = resolveNextPath(searchParams.get("next"), location.pathname);
    const callbackError = searchParams.get("error");

    const completeAuth = async () => {
      if (next === SET_PASSWORD_PATH) {
        const isAuthorized = await ensureAuthorizedStaffSession();
        if (!isAuthorized) {
          setError(true);
          navigate("/login?error=unauthorized", { replace: true });
          return;
        }

        markMustSetPassword();
      }

      navigate(next, { replace: true });
    };

    const failCallback = () => {
      setError(true);
      navigate("/login?error=auth_callback_failed", { replace: true });
    };

    const handleCallback = async () => {
      if (callbackError) {
        failCallback();
        return;
      }

      const hash = window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : window.location.hash;
      const hashParams = new URLSearchParams(hash);
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");

      if (accessToken && refreshToken) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (sessionError) {
          console.error("[AuthCallback] setSession", sessionError.message);
          failCallback();
          return;
        }

        clearAuthHashFromUrl();
        await completeAuth();
        return;
      }

      if (code) {
        const { error: exchangeError } =
          await supabase.auth.exchangeCodeForSession(code);

        if (exchangeError) {
          console.error(
            "[AuthCallback] exchangeCodeForSession",
            exchangeError.message,
          );
          failCallback();
          return;
        }

        await completeAuth();
        return;
      }

      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session) {
        failCallback();
        return;
      }

      await completeAuth();
    };

    void handleCallback();
  }, [searchParams, navigate, location.pathname]);

  if (error) return null;

  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-neutral-500">
      Connexion en cours…
    </div>
  );
}
